// WIP
import React, { useEffect, useMemo, useState } from 'react';

import { combineReducers } from '@reduxjs/toolkit';
import { API_STATUSES } from 'actions/apiStatus';
import { fetchTasksAction, addNewTaskAction } from 'actions/task';
import CardWithHeader, { ModularCardWithHeader } from 'components/CardWithHeader';
import MeshListContext from 'components/MeshList/MeshListContext';
import SectionLoadingView from 'components/MeshList/SectionLoadingView';
import CreateGoalPanel from 'components/MeshList/goals/CreateGoalPanel';
import Goals from 'components/MeshList/goals/Goals';
import tabReducer, { tabReducerInitialState } from 'components/MeshList/tabReducer';
import Tasks from 'components/MeshList/tasks/Tasks';
import { useParsedQuery } from 'components/hooks/useParsedQuery';
import useReducerWithThunk from 'components/hooks/useReducerWithThunk';
import Task1 from 'models/task1';
import { useInView } from 'react-intersection-observer';
import { useSelector, shallowEqual } from 'react-redux';
import { useHistory } from 'react-router-dom';
import apiPaginationMetaReducer, { apiPaginationMetaInitialState } from 'reducers/apiPaginationMeta';
import apiStatusReducer, { apiStatusInitialState } from 'reducers/apiStatus';
import tasksReducer from 'reducers/task';
import {
  completedOnFilterReducer,
  buildCompletedOnFilterInitialState,
} from 'screens/Team/TeamPageFilters/CompletedOnFilter';
import { employeeFilterReducer } from 'screens/Team/TeamPageFilters/EmployeeFilter';
import { selectTeamPageGlobalFilters } from 'selectors/globalFilters';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled, { css } from 'styled-components';
import { StyledTypography, VerticalFlexContainer } from 'styles/common';

import noTasksImage from 'assets/images/task.png';

// import useForceUpdate from 'components/hooks/useForceUpdate';
import { TASK_TYPES_2 } from 'constants/ProjectConstants';
import { generateKey } from 'constants/helper';

import { getStartEndTimeFromDurationKey } from 'services/helpers/task';

import TaskListHeaderV1 from './TaskListHeaderV1';

const { startTS, endTS } = getStartEndTimeFromDurationKey('LASTYEAR');

const filterInitialState = {
  completedOnFilter: buildCompletedOnFilterInitialState({
    from: new Date(startTS),
    to: new Date(endTS),
  }),
  employeeFilter: {},
};

const tabStateWithGoalSelected = { ...tabReducerInitialState, selected: -1 };

const meshListReducerInitialState = {
  tabs: tabStateWithGoalSelected,
  filters: filterInitialState,
  tasks: [],
  apiStatus: apiStatusInitialState,
  apiPaginationMeta: apiPaginationMetaInitialState,
};

function buildMeshInitialState(employeeId) {
  const apiPaginationMeta = { ...apiPaginationMetaInitialState, paginationKey: generateKey(8) };
  const initialState = { ...meshListReducerInitialState, apiPaginationMeta };
  return addCurrentEmployeeFilter(initialState, employeeId);
}

function addCurrentEmployeeFilter(state, employeeId) {
  const newState = { ...state };
  const employeeFilter = { [employeeId]: true };
  newState.filters = { ...state.filters, employeeFilter };
  return newState;
}

const filterReducer = combineReducers({
  completedOnFilter: completedOnFilterReducer,
  employeeFilter: employeeFilterReducer,
});

const meshListReducer = combineReducers({
  tabs: tabReducer,
  filters: filterReducer,
  tasks: tasksReducer,
  apiPaginationMeta: apiPaginationMetaReducer,
  apiStatus: apiStatusReducer,
});

const StyledImage = styled.img`
  max-width: 70%;
`;

const NoTasksView = ({ isGoal = false }) => {
  const text1 = isGoal ? 'You are upto date with all goals' : 'You are upto date with all tasks';
  const text2 = isGoal ? 'Any goals to add?' : 'Any tasks to add?';
  return (
    <VerticalFlexContainer style={{ justifyContent: 'space-around', alignItems: 'center' }}>
      <VerticalFlexContainer style={{ justifyContent: 'center', alignItems: 'center' }}>
        <StyledImage src={noTasksImage} />
        <StyledTypography align="center" as="div" variant="body2" topGutter={3}>
          {text1}
        </StyledTypography>
        <StyledTypography align="center" as="div" variant="body2" topGutter={1} bottomGutter={4} color="textSecondary">
          {text2}
        </StyledTypography>
      </VerticalFlexContainer>
    </VerticalFlexContainer>
  );
};

// TODO: There will be meshlist home page and meshlist team view, two separate views
// and this one will be modular
const ListContainer = ({ employee, type, stats, viewType, source, timeBoundPeriod, selectedCohort = null }) => {
  /*
    Use task reducer
    Things like filter, stats, that will be separate reducers
    I'll call apis inside action and update reducers
    I want to use normalization
    I will use context api to pass data to children
  */
  const [state, dispatch] = useReducerWithThunk(meshListReducer, buildMeshInitialState(employee.id));

  const history = useHistory();

  const parsedSearch = useParsedQuery(history?.location?.search);

  const { ref, inView } = useInView({
    triggerOnce: true,
  });

  const loggedInEmployee = useSelector(selectLoggedInEmployeeData, shallowEqual);
  const globalFilters = useSelector(selectTeamPageGlobalFilters, shallowEqual);

  if (parsedSearch?.teamName === 'My Initiatives') {
    globalFilters.teamId = -1;
  }

  // const [forceUpdateValue, forceUpdate] = useForceUpdate();
  const [isOpenCreateGoalPanel, setIsOpenCreateGoalPanel] = useState(false);
  const [createNewInProgress, setCreateNewInProgress] = useState(false);

  const isLoggedInEmployee = loggedInEmployee?.id === employee?.id;

  const { tasks = [], tabs, apiStatus, apiPaginationMeta } = state;

  const selectedTab = tabs?.selected;
  // const selectedTeamsPageView = teamsPageView?.selectedView;

  const [isTaskCreationPending, setIsTaskCreationPending] = useState(false);
  const apiState = apiStatus.state;

  const contextValue = useMemo(
    () => ({
      selectedTab,
      dispatch,
      employee,
      apiPaginationMeta,
      tasksFilters: {},
      selectedTeamsPageView: type,
      setCreateNewInProgress,
    }),
    [selectedTab, dispatch, JSON.stringify(employee), type]
  );

  useEffect(() => {
    if (!inView) return;
    // dispatch(
    //   apiMetaResetAction({ ...apiPaginationMetaInitialState, paginationKey: generateKey(8) })
    // );

    dispatch(
      fetchTasksAction({
        type: type === 'GOAL' ? TASK_TYPES_2.goal : TASK_TYPES_2.task,
        globalFilters,
        timeBoundPeriod: type === 'GOAL' && source === 'teamPage' ? timeBoundPeriod : null,
        selectedCohort,
      })
    );
  }, [inView]);

  // const onTabChange = newViewTypeId => {
  //   setCreateNewInProgress(false);
  //   if (selectedTab !== newViewTypeId) {
  //     dispatch(tabUpdateSelectionAction(newViewTypeId));
  //     dispatch(
  //       apiMetaResetAction({ ...apiPaginationMetaInitialState, paginationKey: generateKey(8) })
  //     );
  //   }
  // };

  const isLoading = apiState === API_STATUSES.loading;
  const isLoadingMore = apiState === API_STATUSES.loadingMore;

  const createNewHandler = () => {
    if (isLoading) {
      setIsTaskCreationPending(true);
      return;
    }
    setCreateNewInProgress(true);
    if (viewType === 'GOAL') {
      // goals selected
      setIsOpenCreateGoalPanel(true);
    } else {
      dispatch(
        addNewTaskAction({
          employee: { ...employee, ownerType: Task1.EMPLOYEE_TYPES.owner },
          selectedTeamId: globalFilters.teamId,
          orgTag: globalFilters.tagId || undefined,
          ...(globalFilters.tagId && { teams: [] }),
        })
      );
    }
  };

  useEffect(() => {
    if (isTaskCreationPending && !isLoading) {
      createNewHandler();
      setIsTaskCreationPending(false);
    }
  }, [isLoading]);

  return (
    <StyledCardWithHeader
      className="meshlist"
      // disabled={activeTasksMeta.loading}
      header={
        <div ref={ref}>
          <TaskListHeaderV1
            employee={employee}
            // header={header}
            stats={stats}
            // selectedTeam={selectedHeaderDropdownTeam}
            // updateSelectedTeam={updateSelectedTeam}
            // teams={teams}
            // refershTasks={getData}
            type={viewType}
            // isTeamDropdownDisabled={isTeamDropdownDisabled}
            createNewHandler={createNewHandler}
            createNewInProgress={createNewInProgress}
          />
        </div>
      }
      padding={false}
      height="98%"
      displayBorder
      heading="Mesh List"
      // className={className}
    >
      <MeshListContext.Provider value={contextValue}>
        <CardView>
          {/* <MeshListHeader
            createNewInProgress={createNewInProgress}
            createNewHandler={createNewHandler}
            employee={employee}
            refreshHandler={forceUpdate}
          /> */}
          <ContentContainer>
            {/* <StyledTabHeader
              currentTab={selectedTab}
              onTabChange={onTabChange}
              isLoggedInEmployee={isLoggedInEmployee}
            /> */}
            {isLoading ? (
              <SectionLoadingView />
            ) : (
              <>
                {/* <TabViewContainer> */}
                {tasks.length === 0 ? (
                  <NoTasksView isGoal={type === TASK_TYPES_2.goal} />
                ) : type === TASK_TYPES_2.goal ? (
                  <Goals goals={tasks} timeBoundPeriod={timeBoundPeriod} source="Team Page Overall" />
                ) : (
                  <Tasks tasks={tasks} />
                )}

                {isLoadingMore && <SectionLoadingView />}
                {/* </TabViewContainer> */}
              </>
            )}
          </ContentContainer>
        </CardView>
        <CreateGoalPanel
          // forceUpdateParent={forceUpdate}
          open={isOpenCreateGoalPanel}
          onCancel={() => {
            setIsOpenCreateGoalPanel();
            setCreateNewInProgress(false);
          }}
          employee={employee}
          goalListDispatch={dispatch}
          eventSource="Team Page"
        />
      </MeshListContext.Provider>
    </StyledCardWithHeader>
  );
};

const StyledCardWithHeader = styled(CardWithHeader)`
  display: flex;
  flex-direction: column;
  ${(props) =>
    props.disabled &&
    css`
      cursor: initial;
      opacity: 0.65;
      pointer-events: none;
    `}
`;

const CardView = styled(ModularCardWithHeader)`
  height: calc(100% - 120px);
  margin: 0;
`;

const ContentContainer = styled.div`
  height: 100%;
`;

export default {
  NoTasksView,
  ListContainer,
};
